import { useContext, useEffect, useState } from "react";
import {
    SETTING_AI_EXPERIENCE_ACTIVE,
    SETTING_TEMPLATE_CREATE,
    SETTING_TEMPLATE_GET,
    SETTING_TEMPLATE_WATCHDOG,
} from "../../../../helperData/ApiAddress";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import "./styles.scss";
import DBSpinner from "../../../../component/global/DBSpinner";
import DBButton from "../../../../component/global/DBButton";
import defaultPic from "../../../../asset/image/default_photo.png";
import DBConfirmModal from "../../../../component/global/DBConfirmModal";
import WaitModal from "./WaitModal";
import TemplatePreviewModal from "./TemplatePreviewModal";
import AIExperienceModal from "./AIExperienceModal";
import BusinessTypeEditor from "./BusinessTypeEditor";
import { CloseCircle, MagicStar } from "iconsax-react";
import { t } from "i18next";
import { ToastContext } from "../../../../component/global/ToastContext";

const GUIDE_DISMISSED_KEY = "exp-settings-template-guide-dismissed";

export default function TemplateTab({ onTemplateCreated }) {
    const [getTemplate, getTemplateLoader] = useRequest(
        SETTING_TEMPLATE_GET,
        "GET"
    );
    const toast = useContext(ToastContext);

    const [createTemplate, applyLoader] = useRequest(
        SETTING_TEMPLATE_CREATE,
        "POST"
    );

    const [templateWatchDog] = useRequest(SETTING_TEMPLATE_WATCHDOG, "GET");

    const [getActiveDraft] = useRequest(SETTING_AI_EXPERIENCE_ACTIVE, "GET");

    const [templates, setTemplates] = useState([]);
    const [activeDraft, setActiveDraft] = useState(null);
    const [aiModalShow, setAiModalShow] = useState(false);
    const [previewTemplateId, setPreviewTemplateId] = useState(null);
    const [confirmationModalShow, setConfirmationModalShow] = useState(false);
    const [waitModalShow, setWaitModalShow] = useState(false);
    const [guideDismissed, setGuideDismissed] = useState(
        () => localStorage.getItem(GUIDE_DISMISSED_KEY) === "1"
    );

    const getTemplateHandle = () => {
        getTemplate()
            .then((r) => setTemplates(r))
            .catch(() => {});
    };

    const getActiveDraftHandle = () => {
        getActiveDraft()
            .then((r) => setActiveDraft(r?.session || null))
            .catch(() => {});
    };

    useEffect(() => {
        getTemplateHandle();
        getActiveDraftHandle();
    }, []);

    const createTemplateHandle = () => {
        const templateId = confirmationModalShow;
        setConfirmationModalShow(false);
        setWaitModalShow(true);
        createTemplate({
            pattern_type: templateId,
        })
            .then((r) => {
                watchForCreateHandle(r.task);
            })
            .catch((e) => {
                toast(t("template.error"), "danger");
                setWaitModalShow(false);
            });
    };

    const watchForCreateHandle = (taskId) => {
        templateWatchDog({ task_id: taskId })
            .then((r) => {
                if (r.task_status !== "SUCCESS") {
                    setTimeout(() => watchForCreateHandle(taskId), 1000);
                } else {
                    setWaitModalShow(false);
                    onTemplateCreated?.();
                }
            })
            .catch((e) => {
                toast(t("template.error"), "danger");
                setWaitModalShow(false);
            });
    };

    const dismissGuide = () => {
        localStorage.setItem(GUIDE_DISMISSED_KEY, "1");
        setGuideDismissed(true);
    };

    return (
        <div className="template-container">
            <DBConfirmModal
                title={t("template.apply")}
                description={t("template.apply.desc")}
                btnTitle={t("template.apply")}
                onHide={() => setConfirmationModalShow(false)}
                show={confirmationModalShow}
                minWidth={400}
                onConfirm={() => {
                    createTemplateHandle();
                }}
            />
            <WaitModal
                show={waitModalShow}
                onHide={() => setWaitModalShow(false)}
            />
            <TemplatePreviewModal
                templateId={previewTemplateId}
                onHide={() => setPreviewTemplateId(null)}
                onApply={(templateId) => {
                    setPreviewTemplateId(null);
                    setConfirmationModalShow(templateId);
                }}
                applying={applyLoader}
            />
            <AIExperienceModal
                showModal={aiModalShow}
                onHide={() => {
                    setAiModalShow(false);
                    getActiveDraftHandle();
                }}
                onSuccess={() => {
                    getTemplateHandle();
                    getActiveDraftHandle();
                    onTemplateCreated?.();
                }}
            />

            <BusinessTypeEditor onSaved={getTemplateHandle} />

            <section className="ai-cta-card" aria-labelledby="ai-cta-title">
                <div className="ai-cta-icon" aria-hidden="true">
                    <MagicStar size={28} />
                </div>
                <div className="ai-cta-text">
                    <h1 id="ai-cta-title">
                        {t("template.ai.cta.title")}
                        {activeDraft && (
                            <span className="ai-cta-draft-chip">
                                {t("template.ai.cta.draft")}
                            </span>
                        )}
                    </h1>
                    <p>{t("template.ai.cta.description")}</p>
                </div>
                <DBButton
                    caption={
                        activeDraft
                            ? t("template.ai.cta.resume")
                            : t("template.ai.cta.start")
                    }
                    onClick={() => setAiModalShow(true)}
                />
            </section>

            {!guideDismissed ? (
                <section
                    className="template-guide"
                    aria-label={t("template.guide.show")}
                >
                    <button
                        type="button"
                        className="template-guide-close"
                        onClick={dismissGuide}
                        aria-label={t("template.guide.hide")}
                        title={t("template.guide.hide")}
                    >
                        <CloseCircle size={18} aria-hidden="true" />
                    </button>
                    <p>{t("setting-explanation.p1")}</p>
                    <p>{t("setting-explanation.p2")}</p>
                    <p>{t("setting-explanation.p3")}</p>
                    <p>{t("setting-explanation.p4")}</p>
                </section>
            ) : (
                <button
                    type="button"
                    className="template-guide-toggle"
                    onClick={() => setGuideDismissed(false)}
                >
                    {t("template.guide.show")}
                </button>
            )}

            <h2>{t("template.header")}</h2>
            <p className="template-catalog-hint">
                {t("template.catalog.hint")}
            </p>
            <div>
                {getTemplateLoader ? (
                    <DBSpinner />
                ) : templates.length === 0 ? (
                    <p className="template-catalog-empty">
                        {t("template.catalog.empty")}
                    </p>
                ) : (
                    <div className="template-card-con">
                        {templates.map((template) => (
                            <div key={template.id} className="template-card">
                                <div>
                                    <img
                                        src={template.logo || defaultPic}
                                        alt={template.name}
                                        onErrorCapture={(e) =>
                                            (e.target.src = defaultPic)
                                        }
                                    />
                                </div>
                                <h1>{template.name}</h1>
                                {template.source === "ai" && (
                                    <span className="template-source-chip">
                                        {t("template.source.ai")}
                                    </span>
                                )}
                                {template.business_type_labels?.length > 0 && (
                                    <p className="template-type-labels">
                                        {template.business_type_labels.join(
                                            "، "
                                        )}
                                    </p>
                                )}
                                <p>{template.description}</p>
                                <DBButton
                                    caption={t("template.preview.button")}
                                    onClick={() =>
                                        setPreviewTemplateId(template.id)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
